import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/lib/site";
import { Button } from "@/components/ui/button";
import { LeadDialog } from "@/components/LeadDialog";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#FFFFFF]/85 border-b border-[#2C2A29]/10" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" className="flex flex-col leading-none" data-testid="nav-logo">
          <span className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#2C2A29]">Afurnix</span>
          <span className="label-eyebrow text-[10px] text-[#9E8773] -mt-1">Sourcing Atelier</span>
        </Link>

        <div className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={`label-eyebrow text-[11px] pb-1 border-b transition-colors ${
                location.pathname.startsWith(l.to)
                  ? "text-[#2C2A29] border-[#9E8773]"
                  : "text-[#5E5A54] border-transparent hover:text-[#2C2A29]"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="hidden lg:block">
          <LeadDialog
            trigger={
              <Button data-testid="nav-quote-button" className="rounded-none bg-[#2C2A29] hover:bg-[#4A4641] text-[#FFFFFF] px-6 h-10 label-eyebrow text-[10px]">
                Request a Quote
              </Button>
            }
          />
        </div>

        <button className="lg:hidden text-[#2C2A29]" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden bg-[#FFFFFF] border-t border-[#ECEAE4] px-5 py-6 space-y-4" data-testid="mobile-menu">
          {NAV_LINKS.concat([{ label: "Contact", to: "/contact" }]).map((l) => (
            <Link key={l.to} to={l.to} className="block font-display text-2xl text-[#2C2A29]" data-testid={`mobile-link-${l.label.toLowerCase()}`}>
              {l.label}
            </Link>
          ))}
          <LeadDialog
            trigger={
              <Button className="w-full rounded-none bg-[#2C2A29] text-[#FFFFFF] h-12 mt-2" data-testid="mobile-quote-button">
                Request a Quote
              </Button>
            }
          />
        </div>
      )}
    </header>
  );
};
